import React, { useState } from 'react';
import { StyleSheet, Text, View, Pressable } from 'react-native';
import { mdiClose } from '@mdi/js';
import Icon from '@mdi/react';
import GenericButton from '../../components/molecules/GenericButton';
import RejectOfferModal from '../../components/modal/RejectOfferModal';
import CredentialOfferModal from '../../components/modal/CredentialOfferModal';
import { IThemeContext, useThemeContext } from '../../contexts';
import { CredentialExchangeRecord } from '../../types/credentials';

interface CredentialOfferProps {
  credential: CredentialExchangeRecord;
  primaryAction: { title: string; action: () => void };
  secondaryAction: { title: string; action: () => void };
}

/**
 * @description Credential offer component
 * @param {CredentialOfferProps} props - Component properties
 * @return {React.FC}
 */
const CredentialOffer: React.FC<CredentialOfferProps> = ({
  credential,
  primaryAction,
  secondaryAction
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [rejectVisible, setRejectVisible] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const { theme } = useThemeContext();
  const styles = createCredentialOfferStyles(theme);

  const handleCloseModal = () => setIsVisible(false);
  const handleOpenModal = () => setIsVisible(true);
  const handleRejectOffer = (event: any) => {
    event.stopPropagation();
    setRejectVisible(true);
  };

  const handleAcceptOffer = () => {
    setAccepted(true);
    primaryAction.action();
  };

  const handleRejectFromModal = () => {
    setRejectVisible(true);
  };

  return (
    <>
      {/* Credential Offer Modal */}
      <CredentialOfferModal
        visible={isVisible}
        onClose={handleCloseModal}
        accepted={accepted}
        credential={credential}
        primaryAction={primaryAction}
        secondaryAction={secondaryAction}
        onAccept={handleAcceptOffer}
        onReject={handleRejectFromModal}
      />

      {/* Offer List Item */}
      <Pressable onPress={handleOpenModal}>
        <View style={styles.container}>
          {/* Close Icon */}
          <Pressable style={styles.closeIcon} onPress={handleRejectOffer}>
            <Icon path={mdiClose} size={0.8} color={theme.textSecondary} />
          </Pressable>

          {/* Content Container */}
          <View style={styles.contentContainer}>
            {/* Text Section */}
            <View style={styles.textContainer}>
              <Text style={styles.offerText} numberOfLines={3} ellipsizeMode="tail">
                <Text style={styles.offerDetailText}>{credential.issuer}</Text> has offered you a{' '}
                <Text style={styles.offerDetailText}>{credential.name}</Text>.
              </Text>
              <Text style={styles.offerTimeText}>{credential.receivedTimeAgo}</Text>
            </View>

            {/* Action Section */}
            <View style={styles.actionContainer}>
              <GenericButton
                title="View Offer"
                onPress={handleOpenModal}
                isPrimary={true}
                textStyle={styles.viewOfferButtonText}
                buttonStyle={styles.viewOfferButton}
              />
            </View>
          </View>
        </View>
      </Pressable>

      {/* Reject Modal */}
      <RejectOfferModal
        modalVisible={rejectVisible}
        setModalVisible={setRejectVisible}
        rejectAction={secondaryAction.action}
      />
    </>
  );
};

const createCredentialOfferStyles = (
  theme: IThemeContext['theme']
): ReturnType<typeof StyleSheet.create> =>
  StyleSheet.create({
    // Main container styles
    container: {
      position: 'relative',
      borderWidth: 2,
      borderColor: '#D1D5DB',
      borderRadius: 12,
      paddingVertical: 20,
      paddingHorizontal: 16,
      backgroundColor: '#FAFAFA',
      minHeight: 120,
      marginBottom: 8,
      shadowColor: '#000',
      shadowOffset: {
        width: 0,
        height: 2
      },
      shadowOpacity: 0.08,
      shadowRadius: 8,
      elevation: 3
    },

    // Content container (excludes close icon)
    contentContainer: {
      flex: 1,
      gap: 16,
      paddingRight: 32 // Space for close icon
    },

    // Text container styles
    textContainer: {
      flex: 1,
      gap: 8
    },
    offerText: {
      fontSize: 16,
      fontWeight: '400',
      lineHeight: 22,
      color: theme.textPrimary
    },
    offerDetailText: {
      fontWeight: '600',
      color: theme.textPrimary
    },
    offerTimeText: {
      fontSize: 13,
      fontWeight: '400',
      color: theme.textSecondary,
      marginTop: 4
    },

    // Action container styles
    actionContainer: {
      alignItems: 'stretch'
    },
    viewOfferButton: {
      height: 48,
      backgroundColor: theme.primary,
      borderRadius: 8,
      justifyContent: 'center',
      alignItems: 'center',
      shadowColor: '#000',
      shadowOffset: {
        width: 0,
        height: 2
      },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 3
    },
    viewOfferButtonText: {
      fontSize: 16,
      color: 'white',
      fontWeight: '600'
    },

    // Close icon styles
    closeIcon: {
      position: 'absolute',
      right: 16,
      top: 16,
      padding: 8,
      zIndex: 1,
      borderRadius: 20,
      backgroundColor: 'rgba(0, 0, 0, 0.05)'
    }
  });

export default CredentialOffer;
